import { TriangleAlert, X } from 'lucide-react';
import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import { IconButton } from './primitives/controls.tsx';

interface Note {
  id: number;
  lines: string[];
}

const ToastContext = createContext<{
  done: (said: string, warn?: boolean) => void;
  refused: (violations: string[]) => void;
} | null>(null);

export function useToast() {
  const toast = useContext(ToastContext);
  if (!toast) throw new Error('useToast outside a <Toasts>');
  return toast;
}

export function Toasts({ children }: { children: ReactNode }) {
  const [notes, setNotes] = useState<Note[]>([]);

  const dismiss = useCallback((id: number) => {
    setNotes((current) => current.filter((note) => note.id !== id));
  }, []);

  const push = useCallback((lines: string[]) => {
    const id = Date.now() + Math.random();
    setNotes((current) => [...current.slice(-2), { id, lines }]);
  }, []);

  const api = useMemo(
    () => ({
      // Plain success is silent: the row the socket moved is the receipt.
      done: (said: string, warn = false) => {
        if (warn) push([said]);
      },
      refused: (violations: string[]) => push(violations),
    }),
    [push],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div
        aria-live="polite"
        className="pointer-events-none fixed inset-x-0 bottom-[var(--tab-bar)] z-50 flex flex-col items-center gap-2 p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:p-4"
      >
        {notes.map((note) => (
          <output
            key={note.id}
            className="toast-in pointer-events-auto flex w-full max-w-md items-start gap-2.5 rounded-box border border-warn/40 bg-surface-raised px-3.5 py-2.5 text-xs text-ink shadow-lg"
          >
            <TriangleAlert aria-hidden className="mt-px size-3.5 shrink-0 text-warn" />
            <span className="flex min-w-0 flex-col gap-0.5">
              {note.lines.map((line) => (
                <span key={line}>{line}</span>
              ))}
            </span>
            <IconButton
              label="Dismiss"
              onClick={() => dismiss(note.id)}
              className="-mr-1.5 shrink-0"
            >
              <X aria-hidden className="size-3.5" />
            </IconButton>
          </output>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
